import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { GoalActionBlockedVariant } from "web_myplann/Goals/Components/GoalActionBlockedDrawer";
import { isGoalEditable } from "web_myplann/Goals/Helpers/isGoalEditable";
import { useGoalAdvisorChat } from "web_myplann/Goals/Hooks/useGoalAdvisorChat";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type GoalActionLock = {
	blockedVariant: GoalActionBlockedVariant | null;
	openBlocked: (variant: GoalActionBlockedVariant) => void;
	closeBlocked: () => void;
	requestEdit: () => void;
	onLockedAction: (() => void) | null;
	messageAdvisor: () => void;
};

export const useGoalActionLock = (
	goal: GoalDetail | undefined,
): GoalActionLock => {
	const { t } = useResource();
	const navigate = useNavigate();
	const openChat = useGoalAdvisorChat(goal);
	const [blockedVariant, setBlockedVariant] =
		useState<GoalActionBlockedVariant | null>(null);

	const openBlocked = (variant: GoalActionBlockedVariant) =>
		setBlockedVariant(variant);
	const lockedByAdvisor = goal !== undefined && !goal.allowEditByClient;

	const requestEdit = () => {
		if (!goal) {
			return;
		}
		if (isGoalEditable(goal)) {
			navigate(Routing.Goals.GoalFormEdit.path(Number(goal.id)));
			return;
		}
		openBlocked(goal.allowEditByClient ? "completed" : "locked");
	};

	const messageAdvisor = () => {
		setBlockedVariant(null);
		openChat(
			t(Resources.Goals.Detail.Blocked.advisorMessage, {
				goal: goal?.name ?? "",
			}),
		);
	};

	return {
		blockedVariant,
		openBlocked,
		closeBlocked: () => setBlockedVariant(null),
		requestEdit,
		onLockedAction: lockedByAdvisor ? () => openBlocked("locked") : null,
		messageAdvisor,
	};
};
